import { useState } from "react";
import { FaPlus, FaPencilAlt, FaTrash } from "react-icons/fa";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Table } from "../../components/Table";
import { TableCell } from "../../components/Table/TableCell";
import { TableRow } from "../../components/Table/TableRow";
import { TableHead } from "../../components/Table/TableHead";
import { TableBody } from "../../components/Table/TableBody";
import { IconButtonDropdown } from "../../components/IconButtonDropdown";
import { IconButtonDropdownOption } from "../../components/IconButtonDropdown/IconButtonDropdownOption";
import { ConfirmModal } from "../../components/Modals/ConfirmModal";
import { useAccessRoles } from "../../data/useAccessRoles";
import { useDeleteAccessRole } from "../../data/useDeleteAccessRole";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { AccessRole } from "../../types/AccessRole";
import { unwrapList } from "../../utils/unwrapList";
import {
  RolePermissionsSlider,
  DELETE_ROLE_CONFIRM_TEXT,
  deleteRoleErrorMessage,
} from "../../components/Modals/RolePermissionsSlider";
import { formatDate } from "../../utils/formatters";
import { useAuth } from "../../context/AuthContext";

/**
 * Settings > Account & Access > Roles & Permissions (SEG-S021). Each row's
 * three-dot menu offers Edit (opens RolePermissionsSlider) and, for Custom
 * Roles, Delete — System Roles show no delete affordance since they ship
 * built-in.
 */
const SettingsRolesPermissions = () => {
  const { hasPermission } = useAuth();
  const { data: rolesData, isLoading } = useAccessRoles();
  const roles = unwrapList<AccessRole>(rolesData?.data);

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [pendingDeleteRole, setPendingDeleteRole] = useState<AccessRole | null>(
    null
  );

  const { mutate: deleteAccessRole } = useDeleteAccessRole();

  const handleCreateRole = () => {
    setSelectedRoleId(null);
    setIsSliderOpen(true);
  };

  const handleEditRole = (role: AccessRole) => () => {
    setSelectedRoleId(role.id);
    setIsSliderOpen(true);
  };

  const confirmDeleteRole = () => {
    if (!pendingDeleteRole) return;

    deleteAccessRole(
      { id: pendingDeleteRole.id },
      {
        onSuccess: () => {
          notify("Role deleted", { type: "success" });
          queryClient.invalidateQueries(["accessRoles"]);
        },
        onError: (err: any) =>
          showError(err, deleteRoleErrorMessage(err)),
        onSettled: () => setPendingDeleteRole(null),
      }
    );
  };

  const selectedRole = roles.find((role) => role.id === selectedRoleId) ?? null;

  return (
    <Box bg="white" borderRadius={0} p={3} mb={3}>
      <Flex justifyContent="space-between" alignItems="center">
        <H2>Roles & Permissions</H2>
        <Button
          onClick={handleCreateRole}
          Icon={FaPlus}
          iconSize="16px"
          isDisabled={!hasPermission("roles.manage")}
          title={
            hasPermission("roles.manage")
              ? undefined
              : "You don't have permission to manage roles"
          }
        >
          Create Role
        </Button>
      </Flex>

      {isLoading && (
        <Flex justifyContent="center" p={4}>
          <Loader />
        </Flex>
      )}

      {!isLoading && roles.length === 0 && (
        <Paragraph textAlign="center">No roles found.</Paragraph>
      )}

      {!isLoading && roles.length > 0 && (
        <Box mt={5} fontSize={1}>
          <Table>
            <TableHead></TableHead>
            <TableBody>
              <TableRow>
                <TableCell isHeading>Name</TableCell>
                <TableCell isHeading>Type</TableCell>
                <TableCell isHeading>Scope</TableCell>
                <TableCell isHeading>Last modified</TableCell>
                <TableCell isHeading>Users assigned</TableCell>
                <TableCell />
              </TableRow>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.is_system ? "System" : "Custom"}</TableCell>
                  <TableCell>{role.scope_display}</TableCell>
                  <TableCell>{formatDate(role.updated_at)}</TableCell>
                  <TableCell>{role.assigned_user_count}</TableCell>
                  <TableCell>
                    {!role.is_deleted && (
                      <IconButtonDropdown placement="bottom-end">
                        <IconButtonDropdownOption
                          onClick={
                            hasPermission("roles.manage")
                              ? handleEditRole(role)
                              : undefined
                          }
                          isDisabled={!hasPermission("roles.manage")}
                          title={
                            hasPermission("roles.manage")
                              ? undefined
                              : "You don't have permission to manage roles"
                          }
                          IconType={FaPencilAlt}
                          iconSize="14px"
                        >
                          Edit
                        </IconButtonDropdownOption>
                        {!role.is_system && (
                          <IconButtonDropdownOption
                            onClick={
                              hasPermission("roles.manage") &&
                              !role.assigned_user_count
                                ? () => setPendingDeleteRole(role)
                                : undefined
                            }
                            isDisabled={
                              !hasPermission("roles.manage") ||
                              role.assigned_user_count > 0
                            }
                            title={
                              !hasPermission("roles.manage")
                                ? "You don't have permission to manage roles"
                                : role.assigned_user_count > 0
                                ? `Remove the ${role.assigned_user_count} user${
                                    role.assigned_user_count === 1 ? "" : "s"
                                  } assigned to this Role before deleting it`
                                : undefined
                            }
                            IconType={FaTrash}
                            isDestructive
                            iconSize="14px"
                          >
                            Delete
                          </IconButtonDropdownOption>
                        )}
                      </IconButtonDropdown>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <RolePermissionsSlider
        isOpen={isSliderOpen}
        onRequestClose={() => setIsSliderOpen(false)}
        role={selectedRole}
      />

      <ConfirmModal
        isOpen={Boolean(pendingDeleteRole)}
        onRequestClose={() => setPendingDeleteRole(null)}
        handleConfirm={confirmDeleteRole}
        title={
          pendingDeleteRole ? `Delete "${pendingDeleteRole.name}"?` : undefined
        }
        content={DELETE_ROLE_CONFIRM_TEXT}
      />
    </Box>
  );
};

export default SettingsRolesPermissions;
